/** Character-width estimate used wherever a real canvas is unavailable. */
function estimateTextWidth(text: string, fontSize: number): number {
  return text.length * (fontSize * 0.6);
}

/**
 * One canvas for the module's lifetime, resolved once.
 *
 * `undefined` = not yet attempted, `null` = attempted and unavailable (SSR, or
 * jsdom, which throws from `getContext` unless the `canvas` package is
 * installed). Caching the failure matters as much as caching the success:
 * `truncateTextToWidth` binary-searches, so a per-call `createElement` +
 * throw/catch ran O(log n) times per label per render.
 */
let cachedContext: CanvasRenderingContext2D | null | undefined;

function getMeasurementContext(): CanvasRenderingContext2D | null {
  if (cachedContext !== undefined) return cachedContext;

  if (typeof window === "undefined" || typeof document === "undefined") {
    cachedContext = null;
    return cachedContext;
  }

  try {
    cachedContext = document.createElement("canvas").getContext("2d");
  } catch (_e) {
    cachedContext = null;
  }

  return cachedContext;
}

/**
 * Measures the width of text using canvas context
 * Used for dynamic chart margin calculations
 */
function measureTextWidth(text: string, fontSize = 12, fontFamily = "system-ui"): number {
  const context = getMeasurementContext();
  if (!context) return estimateTextWidth(text, fontSize);

  context.font = `${fontSize}px ${fontFamily}`;
  return context.measureText(text).width;
}

/**
 * Calculates the maximum width among multiple text strings
 */
export function getMaxTextWidth(texts: string[], fontSize = 12, fontFamily = "system-ui"): number {
  if (texts.length === 0) return 0;
  return Math.max(...texts.map((text) => measureTextWidth(text, fontSize, fontFamily)));
}

/**
 * Shortens `text` with a trailing ellipsis until it fits within `maxWidth`.
 *
 * For the category axis of a horizontal bar chart, where the label gutter is
 * capped so the bars keep most of the width. Returns the text unchanged when it
 * already fits, so short labels never gain an ellipsis.
 *
 * Binary search rather than a per-character walk: label counts on a categorical
 * chart are unbounded, and this runs on every resize.
 */
export function truncateTextToWidth(text: string, maxWidth: number, fontSize = 12, fontFamily = "system-ui"): string {
  if (maxWidth <= 0) return "";
  if (measureTextWidth(text, fontSize, fontFamily) <= maxWidth) return text;

  const ellipsis = "…";
  // Not even the ellipsis fits; an empty label reads better than a stray dot
  // sitting where a name should be.
  if (measureTextWidth(ellipsis, fontSize, fontFamily) > maxWidth) return "";

  let low = 0;
  let high = text.length;
  while (low < high) {
    const mid = Math.ceil((low + high) / 2);
    if (measureTextWidth(text.slice(0, mid) + ellipsis, fontSize, fontFamily) <= maxWidth) {
      low = mid;
    } else {
      high = mid - 1;
    }
  }

  return text.slice(0, low) + ellipsis;
}
